import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Check, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const ROLES = ['super_admin', 'branch_manager', 'counselor', 'front_desk', 'finance', 'faculty'];
const ROLE_COLOR = { super_admin: '#EF4444', branch_manager: '#8B5CF6', counselor: '#2B6EF0', front_desk: '#10A882', finance: '#E5A100', faculty: '#06B6D4' };
const ROLE_LABELS = { super_admin: 'Super Admin', branch_manager: 'Branch Manager', counselor: 'Counselor', front_desk: 'Front Desk', finance: 'Finance', faculty: 'Faculty' };

const PERMISSIONS = [
  { section: 'Dashboard', permission: 'View dashboard & KPIs', super_admin: true, branch_manager: true, counselor: true, front_desk: true, finance: true, faculty: false },
  { section: 'Leads', permission: 'View leads', super_admin: true, branch_manager: true, counselor: 'own', front_desk: true, finance: false, faculty: false },
  { section: 'Leads', permission: 'Create / edit leads', super_admin: true, branch_manager: true, counselor: true, front_desk: false, finance: false, faculty: false },
  { section: 'Leads', permission: 'Delete leads', super_admin: true, branch_manager: true, counselor: false, front_desk: false, finance: false, faculty: false },
  { section: 'Leads', permission: 'Import / export CSV', super_admin: true, branch_manager: true, counselor: false, front_desk: false, finance: false, faculty: false },
  { section: 'Pipeline', permission: 'View & move leads', super_admin: true, branch_manager: true, counselor: true, front_desk: false, finance: false, faculty: false },
  { section: 'Pipeline', permission: 'Bulk stage changes', super_admin: true, branch_manager: true, counselor: true, front_desk: false, finance: false, faculty: false },
  { section: 'Fees', permission: 'View invoices', super_admin: true, branch_manager: true, counselor: true, front_desk: true, finance: true, faculty: false },
  { section: 'Fees', permission: 'Create invoices', super_admin: true, branch_manager: true, counselor: true, front_desk: false, finance: true, faculty: false },
  { section: 'Fees', permission: 'Edit invoices', super_admin: true, branch_manager: true, counselor: false, front_desk: false, finance: true, faculty: false },
  { section: 'Fees', permission: 'Record payments', super_admin: true, branch_manager: true, counselor: true, front_desk: true, finance: true, faculty: false },
  { section: 'Communication', permission: 'Send messages', super_admin: true, branch_manager: true, counselor: true, front_desk: false, finance: false, faculty: false },
  { section: 'Communication', permission: 'Bulk campaigns', super_admin: true, branch_manager: true, counselor: false, front_desk: false, finance: false, faculty: false },
  { section: 'Communication', permission: 'WhatsApp config', super_admin: true, branch_manager: true, counselor: false, front_desk: false, finance: false, faculty: false },
  { section: 'Automation', permission: 'Create / manage rules', super_admin: true, branch_manager: true, counselor: false, front_desk: false, finance: false, faculty: false },
  { section: 'Reports', permission: 'View analytics', super_admin: true, branch_manager: true, counselor: true, front_desk: true, finance: true, faculty: false },
  { section: 'Reports', permission: 'Export reports', super_admin: true, branch_manager: true, counselor: false, front_desk: false, finance: false, faculty: false },
  { section: 'Branches', permission: 'Create branches', super_admin: true, branch_manager: false, counselor: false, front_desk: false, finance: false, faculty: false },
  { section: 'Branches', permission: 'Edit branches', super_admin: true, branch_manager: 'own', counselor: false, front_desk: false, finance: false, faculty: false },
  { section: 'Settings', permission: 'Manage users & roles', super_admin: true, branch_manager: false, counselor: false, front_desk: false, finance: false, faculty: false },
  { section: 'Settings', permission: 'Pipeline stage config', super_admin: true, branch_manager: false, counselor: false, front_desk: false, finance: false, faculty: false },
  { section: 'Audit Logs', permission: 'View audit logs', super_admin: true, branch_manager: true, counselor: false, front_desk: false, finance: false, faculty: false },
];

function PermCell({ value }) {
  if (value === true) return <Check size={14} weight="bold" className="text-emerald-500 mx-auto" />;
  if (value === 'own') return <span className="text-[9px] font-bold text-amber-500 uppercase">Own</span>;
  return <X size={12} weight="bold" className="text-muted-foreground/25 mx-auto" />;
}

function RolesPermissionsTable() {
  let lastSection = '';
  return (
    <div className="border border-border rounded-xl bg-card overflow-hidden">
      <Table data-testid="roles-permissions-table">
        <TableHeader>
          <TableRow className="bg-secondary/50 hover:bg-secondary/50">
            <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider w-[100px]">Section</TableHead>
            <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Permission</TableHead>
            {ROLES.map(r => (
              <TableHead key={r} className="text-[10px] font-bold h-9 uppercase tracking-wider text-center w-[90px]" style={{ color: ROLE_COLOR[r] }}>{ROLE_LABELS[r]}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {PERMISSIONS.map((p, i) => {
            const showSection = p.section !== lastSection;
            lastSection = p.section;
            return (
              <TableRow key={i} className={showSection ? 'border-t-2 border-border' : ''}>
                <TableCell className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider py-2">{showSection ? p.section : ''}</TableCell>
                <TableCell className="text-[11px] text-foreground py-2">{p.permission}</TableCell>
                {ROLES.map(r => (
                  <TableCell key={r} className="text-center py-2"><PermCell value={p[r]} /></TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

export default function SettingsPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'counselor', phone: '', branch_ids: [] });

  const fetchAll = async () => {
    try { const [u, b] = await Promise.all([api.get('/api/users'), api.get('/api/branches')]); setUsers(u.data.users || []); setBranches(b.data.branches || []); } catch (e) { console.error(e); } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/users', form);
      toast.success('User created');
      setShowCreate(false);
      setForm({ name: '', email: '', password: '', role: 'counselor', phone: '', branch_ids: [] });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await api.put(`/api/users/${userId}`, { role: newRole });
      toast.success('Role updated');
      fetchAll();
    } catch (err) { toast.error('Failed to update role'); }
  };

  const handleStatusToggle = async (u) => {
    const newStatus = u.status === 'active' ? 'inactive' : 'active';
    try {
      await api.put(`/api/users/${u.id}`, { status: newStatus });
      toast.success(`User ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      fetchAll();
    } catch (err) { toast.error('Failed to update status'); }
  };

  const toggleBranchInForm = (branchId) => {
    setForm(prev => {
      const ids = prev.branch_ids.includes(branchId)
        ? prev.branch_ids.filter(id => id !== branchId)
        : [...prev.branch_ids, branchId];
      return { ...prev, branch_ids: ids };
    });
  };

  if (loading) return <div className="text-sm text-muted-foreground text-center py-12 font-mono">loading...</div>;

  return (
    <div data-testid="settings-page" className="stagger-in">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-extrabold text-foreground tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">User management & organization</p>
      </div>

      <Tabs defaultValue="users">
        <TabsList className="bg-secondary rounded-lg h-9"><TabsTrigger value="users" data-testid="tab-users" className="text-xs rounded-md font-mono">Users ({users.length})</TabsTrigger><TabsTrigger value="roles" data-testid="tab-roles" className="text-xs rounded-md font-mono">Roles & Access</TabsTrigger><TabsTrigger value="org" data-testid="tab-org" className="text-xs rounded-md font-mono">Organization</TabsTrigger></TabsList>

        <TabsContent value="users" className="mt-4 space-y-3">
          <div className="flex justify-end">
            {user?.role === 'super_admin' && (
              <Dialog open={showCreate} onOpenChange={setShowCreate}>
                <DialogTrigger asChild><Button data-testid="add-user-btn" className="h-9 rounded-lg text-xs gap-1.5 font-bold text-white" style={{ backgroundColor: '#E04B2A' }}><Plus size={14} weight="bold" /> Add User</Button></DialogTrigger>
                <DialogContent className="rounded-xl max-w-md"><DialogHeader><DialogTitle className="font-display text-foreground text-lg">Add User</DialogTitle></DialogHeader>
                  <form onSubmit={handleCreate} className="space-y-3">
                    <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Name *</Label><Input data-testid="user-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="h-9 rounded-lg text-sm" required /></div>
                    <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Email *</Label><Input data-testid="user-email" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="h-9 rounded-lg text-sm" required /></div>
                    <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Password *</Label><Input data-testid="user-password" type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="h-9 rounded-lg text-sm" required /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Role</Label>
                        <select data-testid="user-role" value={form.role} onChange={e => setForm({...form, role: e.target.value})} className="h-9 w-full rounded-lg text-sm border border-border px-3 bg-card text-foreground">{ROLES.map(r => <option key={r} value={r}>{r.replace('_', ' ')}</option>)}</select></div>
                      <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Phone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="h-9 rounded-lg text-sm" /></div>
                    </div>
                    <div>
                      <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Branches</Label>
                      <div data-testid="user-branch-select" className="flex flex-wrap gap-1.5 mt-1.5 p-2 border border-border rounded-lg bg-card min-h-[36px]">
                        {branches.map(b => (
                          <button
                            key={b.id}
                            type="button"
                            data-testid={`branch-opt-${b.id}`}
                            onClick={() => toggleBranchInForm(b.id)}
                            className={`text-[10px] px-2 py-1 rounded font-mono transition-colors ${
                              form.branch_ids.includes(b.id)
                                ? 'bg-foreground text-background font-bold'
                                : 'bg-secondary text-muted-foreground hover:bg-secondary/80'
                            }`}
                          >
                            {b.name}
                          </button>
                        ))}
                        {branches.length === 0 && <span className="text-[10px] text-muted-foreground">No branches</span>}
                      </div>
                    </div>
                    <Button type="submit" data-testid="submit-user-btn" className="w-full h-9 rounded-lg text-xs font-bold text-white" style={{ backgroundColor: '#E04B2A' }}>Create</Button>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
          <div className="border border-border rounded-xl bg-card overflow-hidden">
            <Table><TableHeader><TableRow className="bg-secondary/50 hover:bg-secondary/50">
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Name</TableHead>
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Email</TableHead>
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Role</TableHead>
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Phone</TableHead>
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Branches</TableHead>
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Status</TableHead>
            </TableRow></TableHeader><TableBody>{users.map(u => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell className="text-[12px] font-semibold py-2.5">{u.name}</TableCell>
                <TableCell className="text-[11px] text-muted-foreground py-2.5 font-mono">{u.email}</TableCell>
                <TableCell className="py-2.5">
                  {user?.role === 'super_admin' ? (
                    <select
                      data-testid={`role-select-${u.id}`}
                      value={u.role}
                      onChange={(e) => handleRoleChange(u.id, e.target.value)}
                      className="text-[10px] font-bold uppercase tracking-wider bg-transparent border border-border rounded px-1.5 py-0.5"
                      style={{ color: ROLE_COLOR[u.role] || '#808080' }}
                    >
                      {ROLES.map(r => <option key={r} value={r}>{r.replace('_', ' ')}</option>)}
                    </select>
                  ) : (
                    <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: ROLE_COLOR[u.role] || '#808080' }}>{u.role?.replace('_', ' ')}</span>
                  )}
                </TableCell>
                <TableCell className="text-[11px] text-muted-foreground py-2.5 font-mono">{u.phone || '-'}</TableCell>
                <TableCell className="text-[11px] text-muted-foreground py-2.5 max-w-[200px] truncate">{u.branch_ids?.map(id => branches.find(b => b.id === id)?.name || id).join(', ') || '-'}</TableCell>
                <TableCell className="py-2.5">
                  {user?.role === 'super_admin' ? (
                    <button
                      data-testid={`status-toggle-${u.id}`}
                      onClick={() => handleStatusToggle(u)}
                      className={`inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider transition-colors ${
                        u.status === 'active' ? 'text-emerald-600' : 'text-muted-foreground/50'
                      }`}
                    >
                      <span className={`w-2 h-2 rounded-full inline-block ${u.status === 'active' ? 'bg-emerald-500' : 'bg-muted-foreground/30'}`} />
                      {u.status || 'active'}
                    </button>
                  ) : (
                    <span className={`w-2 h-2 rounded-full inline-block ${u.status === 'active' ? 'bg-emerald-500' : 'bg-muted-foreground/30'}`} />
                  )}
                </TableCell>
              </TableRow>
            ))}</TableBody></Table>
          </div>
        </TabsContent>

        <TabsContent value="roles" className="mt-4">
          <RolesPermissionsTable />
        </TabsContent>

        <TabsContent value="org" className="mt-4">
          <Card className="border border-border rounded-xl max-w-lg"><CardContent className="p-5 space-y-3">
            <h3 className="font-display text-sm font-bold text-foreground">Organization</h3>
            <div className="space-y-2 text-[12px]">
              {[['Name', 'Admitrix Academy Group'], ['Branches', branches.length], ['Users', users.length], ['Currency', 'INR'], ['Timezone', 'Asia/Kolkata'], ['Academic Year', '2026-27']].map(([k, v]) => (
                <div key={k} className="flex gap-3"><span className="text-muted-foreground w-28 text-[11px]">{k}</span><span className="text-foreground font-mono">{v}</span></div>
              ))}
            </div>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
